import {
	FormItem,
	TextInput,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { isNotNil } from "client/Misc/utils";
import { createValidationConfiguration } from "client/Validation";
import {
	doesMatchRegExp,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { AppType } from "client/Api/Apps/common";
import {
	conditionalAccessValidator,
	initialConditionalAccessState,
	prepareConditionalAccessForSaving
} from "../ConditionalAccess";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import {
	initialMaintenanceWindowState,
	maintenanceWindowValidator,
	prepareMaintenanceWindowForSaving
} from "../MaintenanceWindow";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	EditLocalAppViewModel,
	SaveLocalAppAppModel,
	saveLocalApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditLocalAppProps = {
	initialSettings: EditAppSettings<EditLocalAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditLocalApp = forwardRef(
	(props: EditLocalAppProps, ref: Ref<EditAppHandle>): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveLocalAppAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const prepareModelForSaving = useCallback(
			(model: SaveLocalAppAppModel): SaveLocalAppAppModel => {
				return {
					...model,
					maintenanceSettings: prepareMaintenanceWindowForSaving(model.maintenanceSettings),
					conditionalAccessSettings: prepareConditionalAccessForSaving(
						model.conditionalAccessSettings
					)
				};
			},
			[]
		);

		const formHandler: IFormHandler<SaveLocalAppAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveLocalAppAppModel>(
			formHandler,
			saveLocalApp,
			AppType.LocalApp,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("instructionId")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<FormItem
					label={localize("_InstructionId")}
				>
					<TextInput
						{...formHandler.for("instructionId")
							.attachToTextInput()
						}
						placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
					/>
					<ValidationError
						{...formHandler
							.for("instructionId")
							.attachToValidationError()
						}
					/>
				</FormItem>
			</EditAppBase>
		);
	}
);
EditLocalApp.displayName = "EditLocalApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditLocalAppViewModel>
): SaveLocalAppAppModel {
	const {
		instructionId,
		maintenanceWindow,
		conditionalAccessSettings
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		instructionId,
		maintenanceSettings: initialMaintenanceWindowState(maintenanceWindow),
		conditionalAccessSettings: initialConditionalAccessState(conditionalAccessSettings)
	};
}


const validation = createValidationConfiguration<SaveLocalAppAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forObject("conditionalAccessSettings")
	.when(isNotNil, conditionalAccessValidator());

validation.forObject("maintenanceSettings")
	.when(isNotNil, maintenanceWindowValidator());

validation.forScalar("instructionId")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesMatchRegExp(
			/^([\da-fA-F]){8}-([\da-fA-F]){4}-([\da-fA-F]){4}-([\da-fA-F]){4}-([\da-fA-F]){12}$/,
			localize("_InvalidInput")
		)
	);